import { useNavigate } from 'react-router-dom';

import { AppIcon, ICON_SIZES } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useGetEmployersByNameQuery } from '@backrs_ui/grow-ui/src/redux/employer/employer.api';
import { UserProfileResponse } from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { Box, Chip, Avatar, Typography, SxProps, Theme } from '@mui/material';

import { PublicProfileSection } from './PublicProfileSection';

const PUBLIC_PROFILE_STRINGS = appStrings.modules.profile.public;

type SnapshotEmployerSectionProps = Readonly<{
  publicProfile?: UserProfileResponse;
}>;

export const SnapshotEmployerSection = ({ publicProfile }: SnapshotEmployerSectionProps) => {
  const navigate = useNavigate();

  const employerNameList = publicProfile?.profile?.careerEmployer;

  const { data: employerData } = useGetEmployersByNameQuery(employerNameList || []);

  // for any employers that we don't have a match for, we will just display a non-clickable chip
  const employersNotInDatabase: string[] =
    employerNameList?.filter(
      (employer) => !employerData?.find((e) => e.name.toLowerCase() === employer.toLowerCase()),
    ) || [];

  if (!employerNameList || employerNameList?.length === 0) {
    return null;
  }

  return (
    <PublicProfileSection
      title={PUBLIC_PROFILE_STRINGS['role']}
      variant={{
        type: 'CHILDREN',
        children: (
          <Box>
            {publicProfile?.profile?.careerOccupation && (
              <Box sx={iconContainerSx}>
                <AppIcon iconName="Colors" diameter="16px" color="Colors/Base/Black" />
                <Typography variant="text-sm-medium">
                  {publicProfile?.profile?.careerOccupation?.length &&
                  publicProfile?.profile?.careerOccupation?.length > 30
                    ? publicProfile?.profile?.careerOccupation?.slice(0, 30) + '...'
                    : publicProfile?.profile?.careerOccupation}
                </Typography>
              </Box>
            )}

            {/* DISPLAY FOUND EMPLOYERS IN THE DATABASE */}
            {employerData &&
              employerData?.length > 0 &&
              employerData.map((employer) => (
                <Chip
                  id={`snapshot-employer-chip-${employer?.slug}`}
                  key={employer?.slug}
                  label={employer?.name}
                  variant="tag"
                  size="md"
                  sx={chipSx(true)}
                  onClick={() => {
                    navigate(`${APP_ROUTE.EMPLOYER}/${employer?.slug}`);
                  }}
                  avatar={
                    <Avatar src={employer?.logo_media_url || '/images/employer-logo-default.svg'} />
                  }
                />
              ))}

            {/* DISPLAY NON-FOUND EMPLOYERS AS NON-CLICKABLE CHIPS */}
            {!!employersNotInDatabase.length &&
              employersNotInDatabase.map((employer) => (
                <Chip key={employer} label={employer} variant="tag" size="md" sx={chipSx(false)} />
              ))}
          </Box>
        ),
      }}
    />
  );
};

const iconContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  columnGap: backrs.spacing['spacing-xs'],
  mb: backrs.spacing['spacing-md'],
});

const chipSx =
  (clickable: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    backgroundColor: backrs.colors['Colors/Base/White'],
    cursor: clickable ? 'pointer' : 'default',
    '&:hover': {
      opacity: 0.8,
    },
    width: 'fit-content',
    mb: backrs.spacing['spacing-xs'],
    '& .MuiChip-avatar': {
      marginLeft: `${backrs.spacing['spacing-none']} !important`,
      marginRight: `${backrs.spacing['spacing-xxs']} !important`,
      border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
      width: ICON_SIZES['16px'],
      height: ICON_SIZES['16px'],
      borderRadius: backrs.radius['radius-full'],
    },
  });
